import { sha256 } from '@noble/hashes/sha2.js'
import { bytesToHex } from '@noble/hashes/utils.js'
import { EventTemplate, NostrEvent } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { computeBlobSha256, createUploadAuth, mirrorBlob, uploadBlob } from '@nostr/tools/nipb7'
import { naddrEncode } from '@nostr/tools/nip19'
import { getDefaultStore } from 'jotai'
import client from '@/services/client.service'
import { accountAtom, signerAtom } from '@/atoms/session'
import { blossomServersAtom, relayListAtom } from '@/atoms/lists'
import { pubkeyToB36 } from '@/lib/base36'
import { now } from '@/lib/utils'

// 44billion relay hints — appended to the publish target list so the new nsite
// manifest gets indexed across them too.
export const BILLIONAIRE_RELAYS = [
  'wss://relay.44billion.net',
  'wss://relay.ditto.pub',
  'wss://relay.dreamith.to'
] as const

// Suggested nsite gateways. These follow the convention of hosting nsites at
// `<subdomain>.nsite.lol`; the canonical subdomain form per NIP-5A is
// `<50-char-pubkeyB36><dTag>.host`, but most gateways also expose the short
// `<dTag>.host` form.
export const NSITE_GATEWAYS = [
  { label: 'nsite.lol', urlFor: (sub: string) => `https://${sub}.nsite.lol` },
  { label: '44billion.net', urlFor: (sub: string) => `https://44billion.net/${sub}` }
] as const

export type NsiteDeployStage =
  | { stage: 'idle' }
  | { stage: 'fetching'; url: string }
  | { stage: 'injecting' }
  | { stage: 'uploading'; file: string; server: string }
  | { stage: 'mirroring'; server: string; file: string }
  | { stage: 'signing' }
  | { stage: 'publishing'; relay: string; done: number; total: number }
  | { stage: 'done'; eventId: string; naddr: string; pubkey: string }
  | { stage: 'error'; error: string }

export type DeployableFile = {
  // Absolute path on the site, must start with "/".
  path: string
  // Raw content as a Uint8Array — we always work with bytes so hashes are
  // computed deterministically.
  content: Uint8Array
  // MIME type, used as the Content-Type on upload.
  type: string
}

export type DeployNsiteParams = {
  // d tag value (site identifier). 1-13 chars, lowercase, digits and dashes,
  // cannot end with "-". Becomes the subdomain on nsite gateways.
  d: string
  // description tag content (free text).
  description: string
  // The Hallway universe config that will be baked into the deployed
  // index.html — must be JSON-serializable.
  universe: Record<string, unknown>
  // Optional human-readable site title.
  title?: string
  // Base URL containing /assets.json and all files listed by it.
  sourceURL?: string
  // Existing nsite naddr to clone instead of using sourceURL.
  forkFromNsite?: string
  // Additional blossom servers beyond the ones in the user's BUD-03 server
  // list. Common defaults are the same servers used in
  // window.hallway.universe.defaultBlossomServers.
  extraBlossomServers?: string[]
}

export type DeployNsiteResult = {
  eventId: string
  naddr: string
  pubkey: string
  pathTags: [string, string, string][]
  blossomUrls: string[]
}

function validateDTag(d: string): void {
  if (!/^[a-z0-9-]{1,13}$/.test(d)) {
    throw new Error('nsite id must be 1-13 chars of lowercase letters, digits and hyphens')
  }
  if (d.endsWith('-')) {
    throw new Error('nsite id must not end with "-"')
  }
}

// Sort + dedupe the servers we will use, dropping entries that fail URL
// parsing so we don't crash the upload loop with a malformed value.
function normalizeBlossomServers(input: string[]): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const raw of input) {
    if (!raw) continue
    try {
      const u = new URL(raw)
      const host = u.host.toLowerCase()
      if (seen.has(host)) continue
      seen.add(host)
      out.push(u.toString().replace(/\/+$/, '') + '/')
    } catch {
      // skip
    }
  }
  return out
}

async function uploadFile(
  file: DeployableFile,
  servers: string[],
  signEvent: (t: EventTemplate) => Promise<NostrEvent>,
  onProgress?: (s: NsiteDeployStage) => void
): Promise<{ hash: string; urls: string[] }> {
  if (servers.length === 0) {
    throw new Error(
      'no blossom servers configured — set up at least one in your BUD-03 server list or in the form'
    )
  }

  const blob = new Blob([file.content], { type: file.type })
  const hash = await computeBlobSha256(blob)
  const urls: string[] = []
  let lastErr: unknown = null

  for (const server of servers) {
    onProgress?.({ stage: 'uploading', file: file.path, server })
    try {
      const desc = await uploadBlob(server, blob, {
        auth: true,
        onAuth: (_srv: string, sha: string) => createUploadAuth(signEvent, sha, { type: 'upload' })
      })
      urls.push(desc.url)
      // Mirror to the remaining servers in the background; mirrorBlob prefers
      // URL-based copies and falls back to a re-upload if the server doesn't
      // have the blob yet.
      for (const mirrorServer of servers) {
        if (mirrorServer === server) continue
        onProgress?.({ stage: 'mirroring', server: mirrorServer, file: file.path })
        try {
          await mirrorBlob(mirrorServer, desc, {
            auth: true,
            onAuth: (_srv: string, sha: string) =>
              createUploadAuth(signEvent, sha, { type: 'upload' })
          })
          urls.push(`${mirrorServer.replace(/\/+$/, '')}/${desc.sha256}`)
        } catch {
          // mirror failure is non-fatal; user can still serve from the
          // primary server.
        }
      }
      return { hash, urls }
    } catch (e) {
      lastErr = e
      // try next server
    }
  }

  throw new Error(
    `failed to upload ${file.path} to any blossom server${lastErr ? ': ' + (lastErr as Error).message : ''}`
  )
}

async function downloadNsiteFiles(
  event: NostrEvent,
  onProgress?: (s: NsiteDeployStage) => void
): Promise<DeployableFile[]> {
  const servers = event.tags
    .filter((tag) => tag[0] === 'server' && tag[1])
    .map((tag) => (tag[1].includes('://') ? tag[1] : `https://${tag[1]}`))
  if (servers.length === 0) throw new Error('source nsite has no Blossom servers')

  const files: DeployableFile[] = []
  for (const tag of event.tags.filter((tag) => tag[0] === 'path')) {
    const [, path, hash] = tag
    if (!path || !hash || !path.startsWith('/') || path.startsWith('//') || path.includes('..')) {
      throw new Error(`invalid nsite path tag: ${path ?? ''}`)
    }

    let response: Response | undefined
    for (const server of servers) {
      const url = `${server.replace(/\/+$/, '')}/${hash}`
      onProgress?.({ stage: 'fetching', url })
      try {
        const candidate = await fetch(url)
        if (candidate.ok) {
          response = candidate
          break
        }
      } catch {
        // try next server
      }
    }
    if (!response) throw new Error(`failed to fetch nsite asset ${path}`)

    files.push({
      path,
      content: new Uint8Array(await response.arrayBuffer()),
      type: response.headers.get('content-type')?.split(';', 1)[0] || mimeTypeFor(path)
    })
  }
  return files
}

// Aggregate hash per NIP-5A: sha256 of `<hash> <path>\n` lines, sorted
// lexicographically. Returns lowercase hex.
async function computeAggregateHash(paths: { path: string; hash: string }[]): Promise<string> {
  const lines = paths
    .map((p) => `${p.hash} ${p.path}\n`)
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
    .join('')
  return bytesToHex(sha256(new TextEncoder().encode(lines)))
}

export async function deployNsite(
  params: DeployNsiteParams,
  onProgress?: (s: NsiteDeployStage) => void
): Promise<DeployNsiteResult> {
  validateDTag(params.d)

  const jotaiStore = getDefaultStore()
  const account = jotaiStore.get(accountAtom)
  const signer = await jotaiStore.get(signerAtom)
  if (!account || !signer) throw new Error('You need to be logged in to publish an nsite')
  if (account.signerType === 'npub')
    throw new Error('Nostr nsite publishing requires a private key signer')

  const signEvent = signer.signEvent.bind(signer) as (t: EventTemplate) => Promise<NostrEvent>

  // ---- 1. build the file list -------------------------------------------------
  let files: DeployableFile[]
  let sourceEvent: NostrEvent | undefined
  if (params.forkFromNsite) {
    sourceEvent = await client.fetchEvent(params.forkFromNsite)
    if (!sourceEvent || sourceEvent.kind !== kinds.NsiteNamed) {
      throw new Error(`could not fetch source nsite ${params.forkFromNsite}`)
    }
    files = await downloadNsiteFiles(sourceEvent, onProgress)
  } else {
    if (!params.sourceURL?.trim()) throw new Error('no source URL provided')
    const sourceURL = params.sourceURL.trim().replace(/\/+$/, '') + '/'
    const manifestURL = new URL('assets.json', sourceURL)
    onProgress?.({ stage: 'fetching', url: manifestURL.href })
    const manifestResponse = await fetch(manifestURL)
    if (!manifestResponse.ok) {
      throw new Error(`failed to fetch ${manifestURL.href}: HTTP ${manifestResponse.status}`)
    }
    const paths: unknown = await manifestResponse.json()
    if (!Array.isArray(paths) || !paths.every((path) => typeof path === 'string')) {
      throw new Error(`${manifestURL.href} must contain an array of file paths`)
    }
    files = []
    for (const path of paths) {
      if (!path.startsWith('/') || path.startsWith('//') || path.includes('..')) {
        throw new Error(`invalid asset path: ${path}`)
      }
      const url = new URL(path.slice(1), sourceURL)
      onProgress?.({ stage: 'fetching', url: url.href })
      const response = await fetch(url)
      if (!response.ok) throw new Error(`failed to fetch ${url.href}: HTTP ${response.status}`)
      files.push({
        path,
        content: new Uint8Array(await response.arrayBuffer()),
        type: response.headers.get('content-type')?.split(';', 1)[0] || mimeTypeFor(path)
      })
    }
  }

  // ---- 2. inject the universe JSON into index.html ----------------------------
  onProgress?.({ stage: 'injecting' })
  files = files.map((f) => {
    if (f.path !== '/index.html') return f
    let html = new TextDecoder().decode(f.content)
    const title = (params.title || (params.universe.appName as string) || '').trim()
    if (title) {
      const safe = title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      html = html.replace('<title>Hallway</title>', `<title>${safe}</title>`)
    }
    html = html.replace(
      'window.hallway.universe = {}',
      `window.hallway.universe = JSON.parse(${JSON.stringify(params.universe)})`
    )
    return { ...f, content: new TextEncoder().encode(html) }
  })

  // ---- 3. upload to blossom ---------------------------------------------------
  const userServers = await jotaiStore.get(blossomServersAtom)
  const servers = normalizeBlossomServers([
    ...userServers,
    ...(params.extraBlossomServers ?? []),
    ...(window.hallway.universe.defaultBlossomServers ?? [])
  ])

  const uploaded: { path: string; hash: string; urls: string[] }[] = []
  for (const file of files) {
    const { hash, urls } = await uploadFile(file, servers, signEvent, onProgress)
    uploaded.push({ path: file.path, hash, urls })
  }

  // ---- 4. assemble the nsite manifest ----------------------------------------
  const pathTags: [string, string, string][] = uploaded.map((u) => ['path', u.path, u.hash])
  const aggregate = await computeAggregateHash(uploaded)

  const tags: string[][] = sourceEvent
    ? sourceEvent.tags.filter(
        (tag) => !['d', 'path', 'x', 'server', 'title', 'description'].includes(tag[0])
      )
    : []
  tags.push(['d', params.d])
  tags.push(...pathTags)
  tags.push(['x', aggregate, 'aggregate'])
  if (params.title) tags.push(['title', params.title])
  else if (sourceEvent) {
    const title = sourceEvent.tags.find((tag) => tag[0] === 'title')
    if (title) tags.push(title)
  }
  if (params.description) tags.push(['description', params.description])
  else if (sourceEvent) {
    const description = sourceEvent.tags.find((tag) => tag[0] === 'description')
    if (description) tags.push(description)
  }
  // Dedupe server tags by hostname and include every blossom server we used.
  const seen = new Set<string>()
  for (const url of uploaded.flatMap((u) => u.urls)) {
    try {
      const h = new URL(url).host
      if (!seen.has(h)) {
        seen.add(h)
        tags.push(['server', `${new URL(url).protocol}//${h}`])
      }
    } catch {
      // skip
    }
  }

  // ---- 5. sign + publish -----------------------------------------------------
  onProgress?.({ stage: 'signing' })
  const draft: EventTemplate = {
    kind: kinds.NsiteNamed,
    content: '',
    created_at: now(),
    tags
  }
  const event = await signEvent(draft)

  // Publish to user's write relays + bigRelays + 44billion relays. We pass
  // the explicit list via specifiedRelayUrls so client.determineTargetRelays
  // doesn't second-guess our selection.
  const relayList = (await jotaiStore.get(relayListAtom)) ?? []

  const writeUrls = relayList.filter((r) => r.write).map((r) => r.url)
  const bigUrls = (window.hallway.universe.bigRelayUrls as string[]) ?? []
  const targetRelays = Array.from(
    new Set([...writeUrls, ...bigUrls, ...BILLIONAIRE_RELAYS].map((s) => s.trim()).filter(Boolean))
  )

  if (targetRelays.length === 0) {
    throw new Error('no publish targets — log in and configure your write relays')
  }

  // Publish to all target relays in parallel. client.publishEvent fans out to
  // each URL via pool.ensureRelay(). We surface a single progress event so the
  // UI shows the active relay set rather than a misleading per-relay count.
  if (targetRelays[0]) {
    onProgress?.({
      stage: 'publishing',
      relay: targetRelays[0],
      done: targetRelays.length,
      total: targetRelays.length
    })
  }
  try {
    await client.publishEvent(targetRelays, event)
  } catch {
    // client.publishEvent rejects when no relay accepted; for the nsite flow
    // we still want to surface the event id so the user knows the event was
    // signed and may have been partially published. Re-throw to let the
    // caller decide.
    throw new Error('publish failed on all relays — check your connections')
  }

  const pubkey = account.pubkey
  const naddr = naddrEncode({
    pubkey,
    kind: kinds.NsiteNamed,
    identifier: params.d,
    relays: targetRelays.slice(0, 3)
  })

  onProgress?.({
    stage: 'done',
    eventId: event.id,
    naddr,
    pubkey
  })

  return {
    eventId: event.id,
    naddr,
    pubkey,
    pathTags,
    blossomUrls: uploaded.flatMap((u) => u.urls)
  }
}

function mimeTypeFor(path: string): string {
  const extension = path.toLowerCase().split('.').pop()
  return (
    {
      css: 'text/css',
      html: 'text/html',
      ico: 'image/x-icon',
      jpeg: 'image/jpeg',
      jpg: 'image/jpeg',
      js: 'text/javascript',
      json: 'application/json',
      png: 'image/png',
      svg: 'image/svg+xml',
      webp: 'image/webp',
      wasm: 'application/wasm'
    }[extension ?? ''] ?? 'application/octet-stream'
  )
}

// Build canonical nsite URL for the given pubkey + d tag.
export function gatewayUrlsFor(pubkeyHex: string, d: string): { label: string; url: string }[] {
  const sub = `${pubkeyToB36(pubkeyHex)}${d}`
  return [{ label: 'nsite.lol', url: `https://${sub}.nsite.lol` }]
}
